import { ScalingType, Scene, SceneType } from 'types/Scene';
import {
  all,
  call,
  CallEffect,
  cancel,
  delay,
  fork,
  put,
  select,
  takeEvery
} from 'redux-saga/effects';
import { getScenes, updateScene } from 'modules/scenes';
import analytics from 'utils/analytics';
import {
  ContentItem,
  MediaLayerTypeEnum,
  ProcessingTask,
  ProcessingTaskStatus,
  ProcessingTaskStatusStatusEnum,
  ScalingModeEnum,
  Scene as ApiScene,
  TransitionTypeEnum,
  TrimmedContentItem
} from 'api';
import {
  Downloading,
  Resolution,
  TargetSettingsDownload,
  VideoInfoDownload
} from 'types/Downloading';
import { Transition, TransitionType } from 'types/Transitions';
import { isNil, last } from 'ramda';
import { getType } from 'deox';
import {
  cancelProcessing,
  defaultDownloadingState,
  download,
  exportTemplate,
  getDownloadingState,
  restoreDownloading,
  updateDownloading
} from 'modules/downloading';
import {
  addContent,
  createTask,
  deleteTask,
  getFileSize,
  getShutterstockVideoOriginalUrl,
  getTaskById
} from 'utils/apiWrapper';
import { PROCESSING_POLL_DELAY } from 'constants/downloadModal';
import { getUserKey } from 'modules/user';
import { getRatioFormatObject } from 'modules/ratioFormat';
import { EffectsWithTime } from 'types/Effects';
import { getAudio } from 'modules/audio';
import { SceneVolume } from 'types/sceneVolume';
import { getSceneVolume } from 'modules/sceneVolume';
import { downloadFile } from 'utils/utils';
import { getProjectTemplate, updateExportProgress } from 'modules/template';
import { getScale } from 'utils/konva';
import { getEffectsWithTime, getGlobalEffectsWithTime } from 'modules/effects';
import { FORM_FACTORS, TargetSettingsAspectRatioEnum } from 'constants/ratioFormats';
import { loadShutterstockImageUrl } from './scenesSaga';
import { addAlert } from 'modules/alert';
import { createCompressResolutionTask } from 'utils/templatesExport';
import { LOW_RES_DIMENSION, MID_RES_DIMENSION } from 'constants/templates';
import { downloadMessages, errorUnknownMessage } from 'constants/errorMessages';
import { messages } from 'messages/alertMessages';
import { Audio } from 'types/Audio';
import { AppState } from 'types/AppState';
import Konva from 'konva';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';
import textSceneFunc from 'utils/konva/textSceneFunc';
import dataURItoBlob from 'utils/dataURIToBlob';
import { HandleLoadingProgress } from 'utils/sagas/uploading';
import { PremiumItemSource } from '../../constants/index';
import { setDownloadingSuccess, setPurchasedItems } from 'modules/checkoutModal';
import { saveProjectFile } from 'modules/projectFles';
import ErrorLogger from '../../components/ErrorLogger';

const cnvdiv = document.createElement('div');

const stage = new Konva.Stage({
  container: cnvdiv,
  width: 0,
  height: 0
});

const getSceneType = (scene: Scene): MediaLayerTypeEnum => {
  switch (scene.type) {
    case SceneType.IMAGE:
      return MediaLayerTypeEnum.Image;
    case SceneType.VIDEO:
      return MediaLayerTypeEnum.Video;
    default:
      throw new Error('Incorrect sceneType');
  }
};

const getApiTransition = (
  prevTransition: Transition | null,
  resolution: Resolution,
  ratio: TargetSettingsAspectRatioEnum
): ApiScene['transition'] | null => {
  switch (prevTransition?.type) {
    case TransitionType.Fade:
      return { type: TransitionTypeEnum.Fade };
    case TransitionType.WipeDiagonal:
      return { type: TransitionTypeEnum.WipeDiagonal };
    case TransitionType.Shape:
      return {
        type: TransitionTypeEnum.Shape,
        url: prevTransition.urls[ratio][resolution]
      };
    default:
      return null;
  }
};

const getSceneImageContent = (scene: Scene): ContentItem => ({
  url: scene.src
});

const getSceneVideoContent = (scene: Scene): TrimmedContentItem => ({
  url: scene.processingSrc || scene.src,
  trimDuration: scene.duration,
  trimStart: scene.trimStart
});

const getSceneContent = (scene: Scene): TrimmedContentItem | ContentItem => {
  if (scene.type === SceneType.IMAGE) {
    return getSceneImageContent(scene);
  }
  return getSceneVideoContent(scene);
};

const getScalingMode = (scene: Scene): ScalingModeEnum => {
  if (scene.scalingMode === ScalingType.FILL) {
    return ScalingModeEnum.Fill;
  }

  return ScalingModeEnum.Fit;
};

const getApiEffects = (arrEffects: EffectsWithTime[]) =>
  arrEffects?.map(
    ({
      textArr,
      font,
      fontSize,
      position,
      textColor,
      name,
      background,
      sceneWidth,
      sceneHeight,
      scalingMode,
      timeStart,
      timeEnd,
      width,
      textAlign,
      isBold,
      isItalic,
      isUnderline,
      line,
      letter
    }) => ({
      text: textArr.join('\n'),
      fontStyle: {
        family: font.src,
        size: fontSize,
        xAlign: textAlign,
        isBold,
        isItalic,
        isUnderline,
        lineHeight: line ? line * fontSize : fontSize,
        letterSpacing: letter || 0
      },
      geometry: {
        x: position.x,
        y: position.y,
        width
      },
      colorStyle: {
        textColor,
        background
      },
      name,
      timeStart,
      timeEnd,
      sceneWidth,
      sceneHeight,
      scalingMode
    })
  ) ?? [];

const createGetApiScene = (
  sceneVolume: SceneVolume,
  resolution: Resolution,
  ratio: TargetSettingsAspectRatioEnum, // "1:1",...
  storeEffects: EffectsWithTime[],
  height: number,
  userKey: string,
  handleProgress: HandleLoadingProgress
) => async (scene: Scene): Promise<ApiScene> => {
  const arrEffects = storeEffects.filter(item => item.sceneId === scene.id);

  const rf = FORM_FACTORS[ratio].ratio;
  const startWidth = rf * height;
  const w = Math.ceil(startWidth) + (startWidth % 2);
  const h = Math.ceil(height) + (height % 2);

  const scale = getScale(scene.scalingMode, w, h);

  const layer = new Konva.Layer();
  stage.add(layer);

  stage.setSize({ width: w / 2, height: h / 2 });

  const result: ApiScene = {
    layers: [
      {
        transform: {
          scale_auto: getScalingMode(scene)
        },
        type: getSceneType(scene),
        content: getSceneContent(scene),
        timeEnd: scene.duration
      }
    ],
    effects: []
  };

  if (arrEffects.length) {
    arrEffects.forEach(effect => {
      const konvaGroup = new Konva.Group({
        id: effect.id,
        size: { width: w, height: h },
        scale: { x: scale * 0.5, y: scale * 0.5 },
        position: { x: 0, y: 0 }
      });
      const textNode = new Konva.Text(effectToKonvaTextSettings({ ...effect }));
      textNode._sceneFunc = textSceneFunc;
      konvaGroup.add(textNode);
      layer.add(konvaGroup);
      layer.draw();
    });

    const blob = dataURItoBlob(layer.getCanvas()._canvas.toDataURL('image/png'));

    const file = new File([blob], 'effect.png', {
      type: 'image/png'
    });

    layer.destroy();

    const url = await addContent(file, userKey, handleProgress);

    result.layers.push({
      type: MediaLayerTypeEnum.Image,
      transform: {
        resize: {
          x: w,
          y: h
        }
      },
      content: {
        url
      },
      timeEnd: scene.duration
    });
  } else {
    handleProgress(100);
  }

  const transition = getApiTransition(scene.prevTransition, resolution, ratio);

  if (!isNil(transition)) {
    result.transition = transition;
  }

  if (scene.type === SceneType.VIDEO) {
    result.isMuted = !sceneVolume?.[scene.id]?.volume;
  }

  return result;
};

export const getApiScenes = async (
  storeScenes: Scene[],
  sceneVolume: SceneVolume,
  resolution: Resolution,
  ratio: TargetSettingsAspectRatioEnum,
  storeEffects: EffectsWithTime[],
  height: number,
  progress: number[],
  userKey: string
): Promise<ApiScene[]> =>
  Promise.all(
    storeScenes
      .filter(item => item.type !== SceneType.EMPTY)
      .map((scene, i) =>
        createGetApiScene(
          sceneVolume,
          resolution,
          ratio,
          storeEffects,
          height,
          userKey,
          e => (progress[i] = e)
        )(scene)
      )
  );

export function* restoreDownloadingSaga() {
  const downloading: Downloading = yield select(getDownloadingState);

  const isDownloadingStarted =
    downloading.status === ProcessingTaskStatusStatusEnum.Running ||
    downloading.status === ProcessingTaskStatusStatusEnum.Queued ||
    downloading.status === ProcessingTaskStatusStatusEnum.Done;

  const isRestorable = !isNil(downloading.taskId) && !isNil(downloading.videoInfo?.targetSettings);

  if (isDownloadingStarted && isRestorable) {
    yield processing(true, downloading.videoInfo.targetSettings);
  } else {
    yield put(updateDownloading(defaultDownloadingState));
    yield put(saveProjectFile());
  }
}

export function* downloadSaga({ payload }: ReturnType<typeof download>) {
  const userKey: string | null = yield select(getUserKey);

  if (isNil(userKey)) {
    return;
  }

  yield put(
    updateDownloading({
      status: ProcessingTaskStatusStatusEnum.Running,
      modalShown: true,
      progress: 0
    })
  );

  yield processing(false, payload);
}

function* actualizeShutterstockUrl(scene: Scene, userKey: string, getterFunction: any) {
  try {
    const src: string = yield call(getterFunction, userKey, scene);
    if (src) {
      scene.src = src;
      yield put(
        updateScene(
          {
            src
          },
          scene.id
        )
      );
    }
  } catch (error) {
    ErrorLogger.trigger(error);
    throw new Error();
  }
}

function* actualizeShutterstockUrls(storeScenes: Scene[], userKey: string) {
  for (const scene of storeScenes) {
    if (scene.source === PremiumItemSource.Shutterstock && scene.type === SceneType.IMAGE) {
      yield actualizeShutterstockUrl(scene, userKey, loadShutterstockImageUrl);
    } else if (scene.source === PremiumItemSource.Shutterstock && scene.type === SceneType.VIDEO) {
      yield actualizeShutterstockUrl(scene, userKey, getShutterstockVideoOriginalUrl);
    }
  }
}

export function* processing(
  isRestore: boolean = false,
  payload: TargetSettingsDownload,
  scenes?: Scene[]
) {
  const processingStartTime = Date.now();
  const userKey: string | null = yield select(getUserKey);

  if (isNil(userKey)) {
    return;
  }

  const storeScenes: Scene[] = scenes || (yield select(getScenes));

  yield call(actualizeShutterstockUrls, storeScenes, userKey);

  let uploading = true;
  const progress = new Array(storeScenes.length);
  progress.fill(0);

  const { ratio, ratioText } = yield select(getRatioFormatObject);
  const sceneEffects: EffectsWithTime[] = yield select(getEffectsWithTime);
  const globalEffects: EffectsWithTime[] = yield select(getGlobalEffectsWithTime);
  const arrEffects = getApiEffects(globalEffects);
  const sceneVolume: SceneVolume = yield select(getSceneVolume);

  yield fork(function* () {
    yield put(updateDownloading({ rasterisationProgress: 0 }));
    while (uploading) {
      const progressValue = progress.reduce((a, b) => a + b) / progress.length;
      yield put(updateDownloading({ rasterisationProgress: progressValue }));
      if (progressValue === 100) {
        uploading = false;
        yield cancel();
      }
      yield delay(100);
    }
  });

  const apiScenes: ApiScene[] = yield call(
    getApiScenes,
    storeScenes,
    sceneVolume,
    payload.resolution,
    ratioText,
    sceneEffects,
    payload.height,
    progress,
    userKey
  );

  const audio: Audio = yield select(getAudio);
  const lastScene = storeScenes[storeScenes.length - 1];
  const videoDuration = lastScene.start + lastScene.duration;

  const videoInfo: VideoInfoDownload = {
    targetSettings: payload,
    startTime: processingStartTime,
    exportFinishedData: {
      length: Math.round(videoDuration / 1000),
      resolution: payload.resolution,
      num_of_scenes: apiScenes.length,
      width: payload.width,
      height: payload.height,
      format: payload.container,
      imageSceneCount: apiScenes.filter(item => last(item.layers).type === MediaLayerTypeEnum.Image)
        .length,
      videoSceneCount: apiScenes.filter(item => last(item.layers).type === MediaLayerTypeEnum.Video)
        .length,
      transitions: storeScenes
        .filter((item, i, { length }) => item.type !== SceneType.EMPTY && i !== length - 1)
        .map(item => item.transition.type)
    }
  };

  let processingInfo: ProcessingTaskStatus = {
    status: ProcessingTaskStatusStatusEnum.Running
  };

  const downloading: Downloading = yield select(getDownloadingState);

  try {
    let taskId: string;

    if (!isRestore) {
      const startWidth = ratio * payload.height;
      const width = Math.ceil(startWidth) - (startWidth % 2);
      const height = Math.ceil(payload.height) - (payload.height % 2);
      taskId = yield call(
        createTask,
        {
          scenes: apiScenes,
          audio: !isNil(audio)
            ? { url: audio.url, trimStart: audio.trimStart, trimDuration: audio.trimDuration }
            : null,
          targetSettings: {
            ...payload,
            width,
            effects: arrEffects.map(
              ({
                sceneWidth,
                sceneHeight,
                scalingMode,
                fontStyle,
                geometry: { x, y, width },
                ...item
              }) => {
                const scale = getScale(scalingMode, width, height);
                return {
                  ...item,
                  fontStyle: {
                    ...fontStyle,
                    size: Math.round(fontStyle.size * scale),
                    letterSpacing: Math.round(fontStyle.letterSpacing * scale)
                  },
                  geometry: {
                    width: Math.round(width * scale),
                    x: Math.round(x * scale),
                    y: Math.round(y * scale)
                  }
                };
              }
            )
          }
        } as ProcessingTask,
        userKey
      );
      yield put(updateDownloading({ taskId }));
    } else {
      taskId = downloading.taskId;
    }

    yield put(
      updateDownloading({
        videoInfo
      })
    );

    yield put(saveProjectFile());

    while (true) {
      yield call(cancelProcessingIfCanceledTask, taskId);

      processingInfo = yield call(getTaskById, taskId, userKey);

      yield put(updateDownloading({ progress: processingInfo.progress }));

      if (
        processingInfo.status === ProcessingTaskStatusStatusEnum.Running ||
        processingInfo.status === ProcessingTaskStatusStatusEnum.Queued
      ) {
        yield delay(PROCESSING_POLL_DELAY);
      } else break;
    }

    if (processingInfo.status === ProcessingTaskStatusStatusEnum.Error) {
      throw new Error();
    }

    yield put(
      updateDownloading({
        progress: processingInfo.progress,
        link: processingInfo.result.url,
        status: ProcessingTaskStatusStatusEnum.Done,
        taskId: null
      })
    );

    yield put(saveProjectFile());

    videoInfo.exportFinishedData.size = yield call(getFileSize, processingInfo.result.url);

    yield call([analytics, analytics.exportFinished], {
      ...videoInfo.exportFinishedData,
      result: 'success',
      export_duration: Math.round((Date.now() - processingStartTime) / 1000)
    });

    yield put(setDownloadingSuccess(true));
    yield put(setPurchasedItems(undefined));
  } catch (e) {
    processingInfo = e.response?.data;
    yield put(
      updateDownloading({
        progress: processingInfo?.progress ?? 100,
        error: processingInfo?.error,
        status: ProcessingTaskStatusStatusEnum.Error,
        taskId: null
      })
    );
    yield put(saveProjectFile());
    yield put(addAlert(messages.error, messages.errorInternalServer));
    yield call([analytics, analytics.exportFinished], {
      ...videoInfo.exportFinishedData,
      result: 'fail',
      export_duration: Math.round((Date.now() - processingStartTime) / 1000)
    });

    yield call(cancelProcessingIfCanceledTask);
  }
}

function* cancelProcessingIfCanceledTask(taskId?: string) {
  const downloadingState: Downloading = yield select(getDownloadingState);

  if (isNil(downloadingState.taskId) || downloadingState.taskId !== taskId) {
    yield cancel();
  }
}

export function* cancelProcessingSaga() {
  const userKey: string | null = yield select(getUserKey);
  const downloading: Downloading = yield select(getDownloadingState);
  const taskId = downloading?.taskId;

  try {
    if (!isNil(taskId) && !isNil(userKey)) {
      yield call(deleteTask, taskId, userKey);
    }

    const startTime = downloading.videoInfo?.startTime;

    yield call([analytics, analytics.exportFinished], {
      result: 'cancel',
      export_duration: startTime ? Math.round((Date.now() - startTime) / 1000) : 0,
      ...downloading.videoInfo?.exportFinishedData
    });
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    yield put(addAlert(errorUnknownMessage.title, errorUnknownMessage.errorUnknown));
  } finally {
    yield put(
      updateDownloading({
        ...defaultDownloadingState
      })
    );
    yield put(saveProjectFile());
  }
}

export function* handleExportProgress(progress: number[], status: { running: boolean }) {
  while (status.running) {
    yield put(updateExportProgress(progress.reduce((arr, x) => arr + x, 0) / progress.length || 1));
    yield delay(100);
  }

  yield put(updateExportProgress(100));
}

export function* exportSceneRes(
  scene: Scene,
  number: number,
  dimension: number,
  progress: number[],
  result: { [key: string]: any }
) {
  const userKey: string | null = yield select(getUserKey);

  let processingInfo: ProcessingTaskStatus;

  const taskId: string = yield call(createCompressResolutionTask, dimension, scene, userKey);

  while (true) {
    processingInfo = yield call(getTaskById, taskId, userKey);

    if (processingInfo.status === ProcessingTaskStatusStatusEnum.Done) {
      break;
    }

    progress[number] = processingInfo.progress;

    yield delay(PROCESSING_POLL_DELAY);
  }

  progress[number] = 100;
  result[scene.id][dimension === LOW_RES_DIMENSION ? 0 : 1] = processingInfo.result.url;
}

export function* exportLowMidRes() {
  const userKey: string | null = yield select(getUserKey);

  if (isNil(userKey)) {
    return;
  }

  const scenes: Scene[] = yield select(getScenes);
  const videoScenes = scenes
    .filter(scene => scene.type === SceneType.VIDEO)
    .filter(scene => scene.size.height > MID_RES_DIMENSION);

  const progress: number[] = new Array(videoScenes.length * 2);

  const status = {
    running: true
  };

  const result = videoScenes.reduce<{ [key: string]: any }>((obj, scene) => {
    obj[scene.id] = [];
    return obj;
  }, {});

  yield fork(handleExportProgress, progress, status);

  yield all(
    videoScenes.reduce<CallEffect[]>((arr, scene) => {
      arr.push(call(exportSceneRes, scene, arr.length - 1, LOW_RES_DIMENSION, progress, result));
      arr.push(call(exportSceneRes, scene, arr.length - 1, MID_RES_DIMENSION, progress, result));
      return arr;
    }, [])
  );

  status.running = false;

  return result;
}

// Template: export
function* exportTemplateSaga() {
  yield put(updateExportProgress(1));

  let lowMidRes: { [key: string]: string };

  try {
    lowMidRes = yield exportLowMidRes();
  } catch (e) {
    yield put(addAlert(downloadMessages.midResTitle, downloadMessages.midRes));
  }

  try {
    const project: Partial<AppState> = yield select(getProjectTemplate);

    const text = {
      version: 1.0,
      ...project,
      scenes: {
        ...project.scenes,
        items: project.scenes.items.map((scene: Scene) => {
          if (scene.type === SceneType.VIDEO && scene.size.height > MID_RES_DIMENSION) {
            return {
              ...scene,
              processingSrc: scene.src,
              midResSrc: lowMidRes[scene.id][1],
              src: lowMidRes[scene.id][0]
            };
          }
          return scene;
        })
      }
    };

    yield call(downloadFile, 'template.json', text);
    yield put(updateExportProgress(0));
  } catch (e) {
    ErrorLogger.trigger(e);
    yield put(addAlert(downloadMessages.downloadTemplateTitle, downloadMessages.downloadTemplate));
  }
}

export default function* downloadingSaga() {
  yield takeEvery(getType(download), downloadSaga);
  yield takeEvery(getType(cancelProcessing), cancelProcessingSaga);
  yield takeEvery(getType(restoreDownloading), restoreDownloadingSaga);
  yield takeEvery(getType(exportTemplate), exportTemplateSaga);
}
